<script lang="ts" setup>
import ProductCard from '~/components/products/product-card.vue'
import type { ProductType } from '~/models/product'
import { dataFetchList } from '~/realm/data/list'
import type { CartType } from '~/models/cart'
import { useAclStore } from '~/store/acl'
import { mapBoxLocation } from '~/shared/location'
import { getProducts } from '~/shared/productListing'
import { CategoryModel, type CategoryType } from '~/models/category'

import type { NestedMenuType } from '../layouts/menu/menu'
import { dataFetchSingle } from '~/realm/data/get'
import { ObjectId } from 'bson'
import superjson from 'superjson'

import { ObjectID } from "bson";
console.log('rendering product-list')
// this needs to be executed both on client and server
superjson.registerCustom<ObjectID, string>(
    {
        isApplicable: (v): v is ObjectID => v instanceof ObjectID,
        serialize: v => v.toHexString(),
        deserialize: v => {
            try {
                return new ObjectID(v);
            } catch (error) {
                console.warn(`Invalid ObjectID: ${v}. Returning null.`);
                return new ObjectID('000000000000000000000000');
            }
        },
    },
    'objectid'
);

const cat = ref<CategoryType | null>(null)
const catList = ref<CategoryType[]>([])
const productList = ref<ProductType[]>([])
const sharedProductList = useState('productList', () => '')
const sharedCategoryList = useState('categoryList', () => '')
const total = ref(0)
const cartList = ref<CartType[]>()
// const loading = ref(true)
const catLoading = ref(true)
const $acl = useAclStore()
const visibleProducts = ref<ProductType[]>(new Array())
const currentPage = ref<number>(1)
const tempList = ref<ProductType[]>()
const isBottomPage = ref<boolean>(false)
const categoryModel = new CategoryModel()
const catMenu = ref([] as NestedMenuType[])
const openCats = ref<string[]>([])
const selectedCats = ref<string[]>([])
const firstLoaded = ref(false)
const allowLoadMore = ref(false)
const props = defineProps({
    categoryId: {
        type: [String, Array as () => string[]],
        default: () => [''],
    },
    limit: {
        type: Number,
        default: 24,
    },
    showCategories: {
        type: Boolean,
        default: true,
    },
    search: {
        type: String,
        default: '',
    },
    noTitle: {
        type: Boolean,
        default: false,
    },
})

watch(() => props.search, () => {
    loadProducts()
})

const emit = defineEmits<{
    (e: 'firstLoad'): void,
    (e: 'loaded'): void,
}>()

onMounted(async () => {
    await mapBoxLocation($acl)
    // await Promise.all([loadProducts(), loadCategories()])
})

const categoryIds = computed((): ObjectId[] => {
    console.log('categoryId', props.categoryId);
    if (Array.isArray(props.categoryId)) {
        return props.categoryId
            .filter((c) => c !== null && c !== '' && typeof c !== 'undefined')
            .map((id) => new ObjectId(id));
    }

    return props.categoryId ? [new ObjectId(props.categoryId)] : [];
});

const loadCategories = async () => {
    if (!props.showCategories) {
        catLoading.value = false
        return [null, []]
    }

    catLoading.value = true
    // root level should have a single top level category
    console.log('showing categories', categoryIds.value?.map((ci) => ci.toString()))
    const filters = {
        parentId: {
            $in:
                categoryIds.value.length > 0 ? categoryIds.value : [new ObjectId('62846dfc7fa37b9d4fad1dcc')]
        }, status: 1
    }
    const fetchCategory = categoryIds.value.length > 0 ? await dataFetchSingle<CategoryType>(
        categoryModel,
        categoryIds.value[0]).then((resp) => {
            // console.log('sub categories ', { parents: categoryIds.value?.map((ci) => ci.toString()), resp })
            cat.value = resp
        }) : null;

    const fetchChildCategory = await dataFetchList(categoryModel, {
        page: 1,
        limit: 999,
        sorts: [
            { field: 'weight', dir: 'asc' },
            { field: 'name', dir: 'asc' },
        ],
        filters,
        include: {
            childs: {
                include: {
                    childs: {
                        include: {
                            childs: {
                                include: {
                                    childs: true,
                                },
                            },
                        },
                    },
                },
            },
        },
    }).then(async (res) => {
        // console.log('catlist?', { filters, res })
        catList.value = res.list

        await loadNestedCategories()
    });

    await Promise.allSettled([
        fetchCategory, fetchChildCategory
    ])
    if (!props.noTitle) {
        if (cat.value) {
            $nav.setTitle(cat.value.name);
        }
    }
    return [cat.value, catList.value]
}

const loadMenus = async (
    parentCategory: NestedMenuType[],
    cat: CategoryType
): Promise<NestedMenuType> => {
    const parentUri =
        parentCategory.length > 0
            ? `${parentCategory[parentCategory.length - 1].uri}/`
            : '/c/'

    // console.log('loadmenus', parentUri, cat.name)
    const menu = {
        id: cat._id?.toString(),
        name: cat.name,
        label: cat.name,
        uri: `${parentUri}${cat._id?.toHexString()}`,
        childs: [],
    } as NestedMenuType

    if (cat.childs) {
        for (let c = 0; c < cat.childs?.length; c++) {
            const child = cat.childs[c]

            const parents = parentCategory.concat([menu])
            const childMenu = await loadMenus(parents, child)
            menu.childs.push(childMenu)
        }
    }
    // console.log('loadedmenus', menu)
    return menu
}

const loadNestedCategories = async () => {
    // console.log('catMenu computed catList', catList.value)
    if (catList.value.length === 0) {
        console.log('no categories')
        catLoading.value = false
        return []
    }
    catLoading.value = true
    try {
        catMenu.value = await Promise.all(catList.value.map(async (cat) => {
            // console.log('catMenu loading subcat', cat)
            const nestedMenu = await loadMenus([], cat)
            // console.log('catMenu loading subcat', cat, nestedMenu)
            return nestedMenu
        }))
    } catch (error) {
        console.error('error loading categories', error)
    } finally {
        catLoading.value = false
    }
}

async function loadProducts() {
    console.log('loadProducts!!!')
    // loading.value = true
    const searchFilter = props.search
        ? {
            $text: { $search: props.search },
        }
        : {}
    if (
        categoryIds.value.length === 0
    ) {
        const res = await getProducts({
            filters: { ...searchFilter },
            page: currentPage.value,
            limit: props.limit,
            sorts: [{ field: '_id', dir: 'desc' }],
        })
        productList.value =
            currentPage.value === 1 ? res.list : [...productList.value, ...res.list]
        total.value = res.count
        // console.log('productList', productList.value)
    } else {
        const res = await getProducts({
            filters: {
                ...searchFilter,
                categories: {
                    $in: categoryIds.value
                },
            },
            page: currentPage.value,
            limit: props.limit,
            sorts: [{ field: '_id', dir: 'desc' }],
        })
        console.log('productList', res.list?.length)
        productList.value =
            currentPage.value === 1 ? res.list : [...productList.value, ...res.list]
        total.value = res.count
    }
    // loading.value = false
    emit('loaded')
    if (!firstLoaded.value) {
        emit('firstLoad')
        firstLoaded.value = true
    }
    return { list: productList.value, total: total.value }
}

const { data, pending: productsLoading, refresh } = await useAsyncData(
    'products',
    async () => {
        if (typeof window !== 'undefined' && !$nav.clientSide) {
            console.log('useAsyncData server only, skipped.')
            return
        }
        console.log('useAsyncData runs...')
        const [productsData,
            // categoriesData
        ] = await Promise.allSettled([
            loadProducts(),
            // loadCategories(),
        ])

        if (typeof window === 'undefined') {
            // console.log('useAsyncData productsData', superjson.stringify(productsData.value))
            const productRes = superjson.stringify(productsData.value)
            sharedProductList.value = productRes
        } else {
            // update it to makesure it does not show when transition to another list or page
            sharedProductList.value = superjson.stringify({ total: 0, list: [] })
        }
        // sharedCategoryList.value = superjson.stringify(categoriesData.value)
    },
    {
        watch: [currentPage, categoryIds, props.search, props.categoryId, props.limit, props.showCategories],
        deep: true,
        server: true,
        // server: true
    })

onMounted(() => {
    // console.log('data? sharedProductList', sharedProductList.value)
    const productRes = sharedProductList.value ? superjson.parse(sharedProductList.value) : { list: [], total: 0 }
    total.value = productRes.total
    productList.value = productRes.list
    // if (sharedCategoryList.value) {
    //   const res = sharedCategoryList.value ? superjson.parse(sharedCategoryList.value): [null, []]
    //   console.log('parsed cat', res)
    //   cat.value = res[0]
    //   catList.value = res[1]
    // }
    // catLoading.value = false
    loadCategories()
    // why the hell useasyncdata does not reload when this component is rerendered
    refresh()
    allowLoadMore.value = true
})

// const productsData = computed(() => {
//   return data.value?.productsData
// })

function checkForBottomPage() {
    // let bottomOfWindow = (window.innerHeight + window.scrollY) >= document.body.scrollHeight
    let bottomOfWindow =
        window.innerHeight + window.pageYOffset + 80 >= document.body.scrollHeight
    isBottomPage.value = false

    if (!productsLoading.value && productList.value?.length < total.value) {
        if (bottomOfWindow) {
            console.log('reached bottom')
            updatePage(currentPage.value + 1)
            console.warn(visibleProducts.value)

            if (visibleProducts.value?.length === productList.value?.length) {
                isBottomPage.value = true
            } else {
                isBottomPage.value = false
            }
        }
    } else {
        isBottomPage.value = true
    }
}

async function updatePage(page: number) {
    currentPage.value = page
    isBottomPage.value = false

    await loadProducts()
}

onMounted(() => {
    window.addEventListener('scroll', checkForBottomPage)
})
onUnmounted(() => {
    window.removeEventListener('scroll', checkForBottomPage)
})
const $nav = useNavStore()

</script>

<template>
    <a-layout>
        <a-layout-content>
            <a-empty description="No record(s) found" v-if="!productsLoading && productList.length == 0" />
            <div v-else class="contentBody">
                <div class="" style="background-color: #f1f1f1">
                    <a-row :gutter="8" class="flex justify-start -md:justify-center -sm:justify-evenly">
                        <template v-for="product in productList" :key="product._id.toString()">
                            <a-col :xs="24" :sm="12" :md="8" :lg="6" class="mb-2">
                                <ProductCard :product="product" :loading="productsLoading" />
                            </a-col>
                        </template>

                        <a-col v-if="productsLoading" :xs="24" :sm="12" :md="8" :lg="6" class="mb-2">
                            <a-spin class="my-4 w-full h-full" tip="Loading..." :spinning="productsLoading"></a-spin>
                        </a-col>
                        <a-back-top />
                    </a-row>
                </div>
            </div>
        </a-layout-content>
        <a-layout-sider :reverse-arrow="true" :collapsible="false" theme="light" breakpoint="md" collapsed-width="3em"
            style="min-height: 90v" class="ALayoutSlider hidden md:inline-block">
            <a-spin :spinning="catLoading" class="w-full h-full">
                <h2 class="px-4 py-2 font-medium" v-if="cat">
                    {{ cat.name }}
                </h2>
                <LayoutsMenuNestedMenu :menus="catMenu" :default-all-open="true" v-if="!catLoading" />
            </a-spin>
        </a-layout-sider>
    </a-layout>
</template>

<style>
.wrap {
    position: relative;
}

.productOnLoad {
    text-align: center;
    border-radius: 4px;
    margin-bottom: 20px;
    padding: 30px 50px;
    margin: 20px 0;
}

.wrap a {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0px;
    left: 0px;
}
</style>
